import {
  appDelay,
  calculateStripePartialFacilitationFeesAndAmount,
  DillPaymentMethod,
  formatMoney,
  Supplier,
  SupplierForBuyer,
  calculateFiservFee,
  getBuyerFiservFeeRate,
} from '@dill/dill-shared';
import { Modal } from '@mui/material';
import { Bank, CreditCard, Info, Plus, Trash, Warning, WarningCircle, X } from 'phosphor-react';
import { useEffect, useMemo, useState } from 'react';
import { openMessageModal, closeMessageModal } from '../../../redux/globalSlices/genericSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { COLORS } from '../../../utils/colors';
import { AppButton } from '../../general/AppButton/AppButton';
import { AppInputField } from '../../general/AppInputField/AppInputField';
import { AppRadioButton } from '../../general/AppRadioButton/AppRadioButton';
import { twMerge } from 'tailwind-merge';
import AppAddPaymentMethodModal from '../../general/AppAddPaymentMethodModal/AppAddPaymentMethodModal';
import {
  deletePaymentMethodByMainBuyer,
  getMainBuyerPaymentMethods,
  getWeekPaymentTransactionsLimitationsForMainBuyer,
  payFixedAmount,
  payInvoices,
} from '../../../redux/services/paymentsService';
import { useParamMainBuyerId } from '../../../utils/hooks/useParamMainBuyerId';
import PaymentConfirmationModal from './PaymentConfirmationModal';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getSubSuppliers } from '../../../redux/services/subSuppliersService';
import moment from 'moment';
import { useStripe } from '@stripe/react-stripe-js';
import AppVerifyDwollaMicroTransactionsModal from '../../general/AppVerifyDwollaMicroTransactionsModal/AppVerifyDwollaMicroTransactionsModal';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { AppSwitch } from '../../general/AppSwitch/AppSwitch';
import { distributeFixedAmountToInvoices } from '../../../utils/distributeFixedAmountToInvoices';
import { APP_ENVIRONMENT } from '../../../utils/constants';
import { getGroupedBuyersSuppliers } from '../../../redux/services/buyersService';
import { updateSelectedSupplierForBuyer } from '../../../redux/globalSlices/invoiceSlice';
import { useIdempotencyKey } from '../../../utils/hooks/useIdempotencyKey';
import {
  IDEMPOTENT_REPLAY_SUBTITLE,
  IDEMPOTENT_REPLAY_TITLE,
  isUnconfirmedPaymentError,
  PAYMENT_UNCONFIRMED_SUBTITLE,
  PAYMENT_UNCONFIRMED_TITLE,
} from '../../../utils/idempotency';

export const AddFixedAmountPaymentsModal = ({
  open,
  handleClose = () => {},
}: {
  open: boolean;
  handleClose: (isNavigate?: boolean) => void;
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const stripe: any = useStripe();
  const params = useParams();
  const { user } = useAppSelector((state) => state.auth);
  const { paymentMethods } = useAppSelector((state) => state.payments);
  const [selectedMainBuyerId] = useParamMainBuyerId();
  const { selectedBuyerSuppliers } = useAppSelector((state) => state.buyers);

  const [isShowErrors, setIsShowErrors] = useState(false);

  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<DillPaymentMethod | null>(
    null
  );
  const [isAppAddPaymentMethodModalOpen, setIsAppAddPaymentMethodModalOpen] = useState(false);
  const [isPaymentConfirmationModalOpen, setIsPaymentConfirmationModalOpen] = useState(false);
  const [remittanceNotes, setRemittanceNotes] = useState('');
  const { selectedSupplierForBuyer } = useAppSelector((state) => state.invoice);
  const { selectedMainBuyer } = useAppSelector((state) => state.buyers);
  const [
    selectedPaymentMethodToVerifyMicroTransactions,
    setSelectedPaymentMethodToVerifyMicroTransactions,
  ] = useState<DillPaymentMethod>();
  const [
    isAppVerifyDwollaMicroTransactionsModalOpen,
    setIsAppVerifyDwollaMicroTransactionsModalOpen,
  ] = useState(false);
  const [isApplyToOldest, setIsApplyToOldest] = useState(true);

  useEffect(() => {
    if (open) {
      setPaymentAmount(null);
      setRemittanceNotes('');
      setSelectedPaymentMethod(null);
      setIsShowErrors(false);
      setIsPaymentConfirmationModalOpen(false);
      setIsAppVerifyDwollaMicroTransactionsModalOpen(false);
      setIsApplyToOldest(true);
    }
  }, [open]);

  const buyerForMainBuyer = useMemo(() => {
    const buyer = user?.userBuyers?.find(
      (buy) =>
        buy.supplierId === selectedSupplierForBuyer?.id &&
        selectedMainBuyer &&
        selectedMainBuyer.quickBooksCustomerId === buy.quickbooksCustomerId
    );
    return buyer;
  }, [selectedSupplierForBuyer, selectedMainBuyer, user?.userBuyers]);

  const fetchedPaymentMethods = useMemo(() => {
    if (paymentMethods.length > 0) {
      if (!selectedPaymentMethod) {
        if (!paymentMethods[0]?.status) {
          setSelectedPaymentMethod(paymentMethods[0] ?? null);
        } else {
          setSelectedPaymentMethod(null);
        }
      }
      return paymentMethods;
    } else {
      setSelectedPaymentMethod(null);
      return [];
    }
  }, [paymentMethods]);

  useEffect(() => {
    if (selectedMainBuyerId && open) {
      dispatch(
        getWeekPaymentTransactionsLimitationsForMainBuyer({ mainBuyerId: selectedMainBuyerId })
      );
      dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId }));
      dispatch(getSubSuppliers({ mainBuyerId: selectedMainBuyerId }));
    }

    return () => {};
  }, [selectedMainBuyerId, open]);

  const selectedSupplier = useMemo(() => {
    let newSupplier: SupplierForBuyer | null = null;

    if (selectedSupplierForBuyer) {
      const supplier: SupplierForBuyer | undefined = selectedSupplierForBuyer;

      if (supplier) {
        newSupplier = supplier;
      } else {
        newSupplier = null;
      }
    }
    return newSupplier;
  }, [location.pathname, selectedSupplierForBuyer, open]);

  const selectedBuyerId = useMemo(() => {
    let selectedId = '';

    if (selectedSupplierForBuyer) {
      selectedId = selectedSupplierForBuyer.buyer.id;
    }

    return selectedId;
  }, [selectedSupplierForBuyer]);

  const creditCardTakeRate = useMemo(() => {
    const buyer = user?.userBuyers?.find(
      (buy) =>
        buy.supplierId === selectedSupplierForBuyer?.id &&
        selectedMainBuyer &&
        selectedMainBuyer.quickBooksCustomerId === buy.quickbooksCustomerId
    );
    let calculateAmount = {} as {
      supplierFees: number;
      buyerFees: number;
      amount: number;
      amountToPay: number;
      totalFees: number;
    };
    if (selectedSupplierForBuyer && buyer) {
      calculateAmount = calculateStripePartialFacilitationFeesAndAmount({
        amount: 100,
        supplier: { ...selectedSupplierForBuyer, creditCardTakeRateExtra: 0 },
        buyer: { ...buyer, creditCardTakeRateExtra: 0 },
      });
    }
    return calculateAmount?.buyerFees ?? 0;
  }, [selectedPaymentMethod]);

  const fees = useMemo(() => {
    const result = fetchedPaymentMethods.find((method) => method.id === selectedPaymentMethod?.id);
    if (
      result &&
      result.type === 'CREDIT_CARD' &&
      result.cardType === 'CREDIT' &&
      result.provider === 'FISERV' &&
      selectedMainBuyer
    ) {
      const amount = paymentAmount || 0;
      let buyerFees = 0;
      const buyer = user?.userBuyers?.find(
        (buy) =>
          buy.supplierId === selectedSupplierForBuyer?.id &&
          selectedMainBuyer.quickBooksCustomerId === buy.quickbooksCustomerId
      );

      if (selectedSupplierForBuyer) {
        const calculateAmount = calculateFiservFee({
          percentageRate: getBuyerFiservFeeRate({
            buyer: buyer,
            supplier: selectedSupplierForBuyer,
          }),
          amount: amount,
        });

        buyerFees = calculateAmount.buyerFees;
      }
      return buyerFees;
    }
    if (
      result &&
      result.type === 'CREDIT_CARD' &&
      result.cardType === 'CREDIT' &&
      result.provider === 'STRIPE' &&
      selectedMainBuyer
    ) {
      const amount = paymentAmount || 0;
      const buyer = user?.userBuyers?.find(
        (buy) =>
          buy.supplierId === selectedSupplierForBuyer?.id &&
          selectedMainBuyer.quickBooksCustomerId === buy.quickbooksCustomerId
      );
      let calculateAmount = {} as {
        supplierFees: number;
        buyerFees: number;
        amount: number;
        amountToPay: number;
        totalFees: number;
      };
      if (selectedSupplierForBuyer) {
        calculateAmount = calculateStripePartialFacilitationFeesAndAmount({
          amount,
          supplier: selectedSupplierForBuyer,
          buyer,
        });
      }
      return calculateAmount?.buyerFees ?? 0;
    }
    return 0;
  }, [paymentAmount, selectedPaymentMethod, selectedMainBuyer]);

  const openInvoices = useMemo(() => {
    if (!selectedSupplierForBuyer?.invoices) return [];
    return [
      ...(selectedSupplierForBuyer.invoices.pastDueInvoices?.invoices || []),
      ...(selectedSupplierForBuyer.invoices.currentInvoices?.invoices || []),
    ];
  }, [selectedSupplierForBuyer]);

  /** Open credits only — the pending ones are already committed to another payment. */
  const openCreditMemos = useMemo(
    () => selectedSupplierForBuyer?.invoices?.creditMemosDetails?.creditMemos || [],
    [selectedSupplierForBuyer]
  );

  const distributionResult = useMemo(() => {
    if (!isApplyToOldest || !paymentAmount || paymentAmount <= 0 || openInvoices.length === 0) {
      return {
        distributedInvoices: [],
        appliedCredits: [],
        totalCreditsUsed: 0,
        excessAmount: 0,
        totalInvoiceAmount: 0,
        totalDiscount: 0,
      };
    }
    // Check 1: Integration type + domain eligibility (mirrors AppPaymentInvoiceTable discountPayment logic)
    const isIntegrationEligible =
      selectedSupplierForBuyer &&
      (selectedSupplierForBuyer?.integrationType === 'MANUAL_UPLOAD' ||
        selectedSupplierForBuyer?.integrationType === 'DDI_MANUAL_UPLOAD' ||
        (selectedSupplierForBuyer?.integrationType === 'P21_MANUAL_UPLOAD' &&
          selectedSupplierForBuyer?.domainId === 'carolinasupply') ||
        (selectedSupplierForBuyer?.integrationType === 'SAGE_100' &&
          selectedSupplierForBuyer?.domainId === 'topgun') ||
        (selectedSupplierForBuyer?.integrationType === 'INFOR_DIST_SX' &&
          !!selectedSupplierForBuyer?.discountSettings?.useErpTermsDiscount) ||
        selectedSupplierForBuyer?.integrationType === 'DISTRIBUTION_ONE' ||
        selectedSupplierForBuyer?.integrationType === 'ECLIPSE' ||
        selectedSupplierForBuyer?.integrationType === 'ECLIPSE_MANUAL_UPLOAD' ||
        (selectedSupplierForBuyer?.integrationType === 'RUTTER_QBD' &&
          APP_ENVIRONMENT !== 'production'));

    // Check 2: Payment method type eligibility
    const paymentMethodResult = paymentMethods.find(
      (method) => method.id === selectedPaymentMethod?.id
    );
    const isPaymentMethodEligible =
      paymentMethodResult &&
      (paymentMethodResult.type === 'BANK_ACCOUNT' ||
        !!selectedSupplierForBuyer?.discountSettings?.isDiscountOnCardPayments);

    const isDiscountEligible =
      !!isIntegrationEligible && !!selectedMainBuyer && isPaymentMethodEligible;

    return distributeFixedAmountToInvoices({
      fixedAmount: paymentAmount,
      openInvoices,
      openCreditMemos,
      integrationType: selectedSupplierForBuyer?.integrationType,
      domainId: selectedSupplierForBuyer?.domainId,
      discountEligible: isDiscountEligible,
    });
  }, [
    isApplyToOldest,
    paymentAmount,
    openInvoices,
    openCreditMemos,
    selectedSupplierForBuyer,
    selectedPaymentMethod,
    paymentMethods,
    selectedMainBuyer,
  ]);

  /**
   * Two independent holders because the "apply to oldest with excess" flow makes
   * *two* payments in one submit, on two different endpoints. They need separate
   * keys, and each has to be retired on its own — sharing one holder would let
   * the second leg evict the first leg's key mid-flow.
   */
  const { resolveKey: resolvePayInvoicesKey, clearKey: clearPayInvoicesKey } = useIdempotencyKey();
  const { resolveKey: resolveFixedAmountKey, clearKey: clearFixedAmountKey } = useIdempotencyKey();

  /**
   * Mirrors the fields `payInvoicesFingerprint` reads server-side, so "same
   * payment" means the same thing on both sides.
   */
  const buildPayInvoicesSignature = (paymentData: any) => ({
    route: 'payInvoices',
    mainBuyerId: selectedMainBuyerId ?? null,
    buyerId: paymentData?.buyerId ?? null,
    receivingSupplierId: paymentData?.receivingSupplierId ?? null,
    senderDillPaymentMethodId: paymentData?.senderDillPaymentMethodId ?? null,
    payDateString: paymentData?.payDateString ?? null,
    totalCreditsToUse: paymentData?.totalCreditsToUse ?? null,
    invoices: (paymentData?.invoices ?? [])
      .map((invoice: any) =>
        [
          invoice?.providerInvoiceId ?? '',
          invoice?.quickbooksInvoiceId ?? '',
          invoice?.invoiceNumber ?? '',
          invoice?.amountPaid ?? 0,
          invoice?.discountAmount ?? 0,
        ].join('|')
      )
      .sort(),
    selectedCreditMemos: (paymentData?.selectedCreditMemos ?? [])
      .map((memo: any) => [memo?.id ?? '', memo?.amount ?? memo?.totalAmount ?? 0].join('|'))
      .sort(),
  });

  /** Mirrors `payFixedAmountFingerprint`. */
  const buildFixedAmountSignature = (paymentData: any) => ({
    route: 'payFixedAmount',
    mainBuyerId: selectedMainBuyerId ?? null,
    buyerId: paymentData?.buyerId ?? null,
    receivingSupplierId: paymentData?.receivingSupplierId ?? null,
    senderDillPaymentMethodId: paymentData?.senderDillPaymentMethodId ?? null,
    paymentAmount: paymentData?.paymentAmount ?? null,
    payDateString: paymentData?.payDateString ?? null,
    remittanceNotes: paymentData?.remittanceNotes ?? null,
  });

  const checkErrors = () => {
    let isError = false;
    if (!paymentAmount || (!isApplyToOldest && !remittanceNotes)) {
      isError = true;
    }
    if (!selectedPaymentMethod) {
      isError = true;
    }
    if (isApplyToOldest && openInvoices.length === 0) {
      isError = true;
    }
    if (isError) {
      setIsShowErrors(true);
    }
    return isError;
  };
  const handlePay = async () => {
    if (checkErrors()) {
      return;
    }
    if (!paymentAmount) {
      return;
    }
    if (!isApplyToOldest && !remittanceNotes) {
      return;
    }

    if (!selectedMainBuyerId) {
      return;
    }

    dispatch(
      openMessageModal({
        buttonText: '',
        modalType: 'LOADING',
        title: 'Processing payment',
        subTitle: 'This may take a few minutes. Please do not refresh the page',
      })
    );

    const handleStripeConfirmation = async (paymentResponse: any) => {
      const dataResults: any = paymentResponse.payload;
      if (dataResults && dataResults?.data?.data?.clientSecret) {
        const clientSecret = dataResults?.data?.data?.clientSecret;
        try {
          const stripeConfirmRes = await stripe?.confirmCardPayment(clientSecret, {});
          if (stripeConfirmRes?.error) {
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'ERROR',
                title: 'Error processing payment',
                subTitle: `${stripeConfirmRes?.error?.message || ''}`,
              })
            );
            return false;
          }
        } catch (error) {
          console.log(error);
          dispatch(
            openMessageModal({
              buttonText: 'Close',
              modalType: 'ERROR',
              title: 'Error processing payment',
              subTitle: 'Error validating payment. Please try again',
            })
          );
          return false;
        }
      }
      return true;
    };

    /**
     * `isReplay` says the server matched this to an earlier attempt rather than
     * charging again. Said out loud, because otherwise someone who meant to pay
     * twice would read a plain success and believe both went through.
     */
    const showSuccess = (isReplay = false) => {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'SUCCESS',
          title: isReplay ? IDEMPOTENT_REPLAY_TITLE : 'Payment successfully sent!',
          subTitle: isReplay
            ? IDEMPOTENT_REPLAY_SUBTITLE
            : `Please check your Transaction History for confirmation of final payment status`,
          onClose: async () => {
            if (selectedMainBuyerId) {
              if (isApplyToOldest) {
                const supplierDomainId =
                  params.supplierDomainId || selectedSupplierForBuyer?.domainId;
                if (supplierDomainId) {
                  navigate(`/companies/${selectedMainBuyerId}/invoices/${supplierDomainId}`);
                }
                dispatch(
                  getGroupedBuyersSuppliers({
                    mainBuyerId: selectedMainBuyerId,
                    weeksLimit: 2,
                  })
                );
              } else {
                navigate(`/companies/${selectedMainBuyerId}/payments`);
                navigate(0);
              }
              handleClose();
            }
          },
        })
      );
    };

    const showError = (message: string) => {
      // An unconfirmed payment is not a failure — the keys stay held so a retry
      // replays, and the buyer is pointed at Transaction History rather than
      // support.
      if (isUnconfirmedPaymentError(message)) {
        dispatch(
          openMessageModal({
            buttonText: 'Close',
            modalType: 'WARN',
            title: PAYMENT_UNCONFIRMED_TITLE,
            subTitle: PAYMENT_UNCONFIRMED_SUBTITLE,
          })
        );
        return;
      }
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error processing payment',
          subTitle: `${message} Contact (628) 304-0931 for customer support`,
        })
      );
    };

    // Case A: Toggle OFF — original open payment behavior
    if (!isApplyToOldest) {
      const fixedAmountPaymentData = {
        paymentAmount: paymentAmount || 0,
        senderDillPaymentMethodId: selectedPaymentMethod?.id || '',
        receivingSupplierId: selectedSupplierForBuyer?.id ?? '',
        buyerId: selectedBuyerId,
        payDateString: moment().format('MM/DD/YYYY'),
        remittanceNotes: remittanceNotes,
      };
      // Reuse an existing key so a resubmission after a timeout or network
      // error is recognised server-side instead of charging the buyer twice.
      const paymentResponse = await dispatch(
        payFixedAmount({
          mainBuyerId: selectedMainBuyerId,
          paymentData: fixedAmountPaymentData,
          idempotencyKey: resolveFixedAmountKey(buildFixedAmountSignature(fixedAmountPaymentData)),
        })
      );
      if (paymentResponse.type === 'payments/payFixedAmount/fulfilled') {
        const confirmed = await handleStripeConfirmation(paymentResponse);
        // Deliberately kept while the Stripe confirmation is unresolved: a retry
        // there must replay the same clientSecret, not create a second charge.
        if (!confirmed) return;
        clearFixedAmountKey();
        showSuccess((paymentResponse.payload as any)?.isReplay === true);
      } else if (paymentResponse.type === 'payments/payFixedAmount/rejected') {
        showError(paymentResponse.payload as any);
      } else {
        dispatch(closeMessageModal());
      }
      return;
    }

    // Case B/C: Toggle ON — distribute to oldest invoices
    const { distributedInvoices, excessAmount, appliedCredits, totalCreditsUsed } =
      distributionResult;
    if (distributedInvoices.length === 0) {
      dispatch(closeMessageModal());
      return;
    }

    // Build payInvoice array matching AppSelectedPayInvoicesModal format
    const hasPartialPay = distributedInvoices.some((d) => d.isPartialPay);
    const payInvoiceArray = distributedInvoices.map((d) => {
      const currentInvoice = d.invoice;
      const paidAmountAfterDiscount = d.amountToPay - d.discountAmount;
      return {
        amount: currentInvoice.amount,
        quickbooksInvoiceId: currentInvoice.platformInvoiceId || '',
        providerInvoiceId: currentInvoice.id,
        quickbooksJobId: currentInvoice?.quickBooksJob?.platformJobId || '',
        providerJobId: currentInvoice?.quickBooksJob?.id || '',
        invoiceNumber: currentInvoice.docNumber,
        amountPaid: Number(paidAmountAfterDiscount.toFixed(2)),
        dueDate: currentInvoice.dueDate,
        openAmount: currentInvoice.balance,
        recordType: currentInvoice.recordType ? currentInvoice.recordType : ('INVOICE' as const),
        discountAmount: Number(d.discountAmount.toFixed(2)),
        jobId: currentInvoice?.jobId || '',
        customerId: currentInvoice?.customerId || '',
        status: currentInvoice?.status || '',
        invoiceCreatedAt: currentInvoice?.createdAt || null,
      };
    });

    const invoiceData = {
      senderDillPaymentMethodId: selectedPaymentMethod?.id || '',
      invoices: payInvoiceArray,
      receivingSupplierId: selectedSupplierForBuyer?.id ?? '',
      buyerId: selectedBuyerId,
      partialPaymentReason: hasPartialPay ? 'PARTIAL_PAY' : '',
      // `invoices` above carry the full amount applied — cash plus credit. The server splits the
      // two with these memos and only charges the bank the difference, which is the fixed amount
      // the buyer entered.
      totalCreditsToUse: totalCreditsUsed,
      partialPaymentNotes: '',
      partialPaymentAttachmentsFiles: [],
      payDateString: moment().format('MM/DD/YYYY'),
      selectedCreditMemos: appliedCredits,
    };

    // Case B: No excess — just pay invoices
    if (excessAmount <= 0) {
      const paymentResponse = await dispatch(
        payInvoices({
          mainBuyerId: selectedMainBuyerId,
          invoiceData,
          sendRelease: false,
          releaseData: [],
          idempotencyKey: resolvePayInvoicesKey(buildPayInvoicesSignature(invoiceData)),
        })
      );
      if (paymentResponse.type === 'payments/pay/fulfilled') {
        const confirmed = await handleStripeConfirmation(paymentResponse);
        if (!confirmed) return;
        clearPayInvoicesKey();
        showSuccess((paymentResponse.payload as any)?.isReplay === true);
      } else if (paymentResponse.type === 'payments/pay/rejected') {
        showError(paymentResponse.payload as any);
      } else {
        dispatch(closeMessageModal());
      }
      return;
    }

    // Case C: With excess — pay invoices first, then excess as open payment
    const paymentResponse = await dispatch(
      payInvoices({
        mainBuyerId: selectedMainBuyerId,
        invoiceData,
        sendRelease: false,
        releaseData: [],
        idempotencyKey: resolvePayInvoicesKey(buildPayInvoicesSignature(invoiceData)),
      })
    );
    if (paymentResponse.type === 'payments/pay/fulfilled') {
      const confirmed = await handleStripeConfirmation(paymentResponse);
      if (!confirmed) return;

      // Now pay the excess as open payment. The invoice leg's key is held, not
      // cleared, until this second leg lands too: the whole submit is one action
      // to the buyer, so a retry of it must replay the invoice payment rather
      // than run it a second time.
      const excessPaymentData = {
        paymentAmount: excessAmount,
        senderDillPaymentMethodId: selectedPaymentMethod?.id || '',
        receivingSupplierId: selectedSupplierForBuyer?.id ?? '',
        buyerId: selectedBuyerId,
        payDateString: moment().format('MM/DD/YYYY'),
        remittanceNotes: 'Excess amount from apply to oldest',
      };
      const excessResponse = await dispatch(
        payFixedAmount({
          mainBuyerId: selectedMainBuyerId,
          paymentData: excessPaymentData,
          idempotencyKey: resolveFixedAmountKey(buildFixedAmountSignature(excessPaymentData)),
        })
      );
      if (excessResponse.type === 'payments/payFixedAmount/fulfilled') {
        const excessConfirmed = await handleStripeConfirmation(excessResponse);
        if (!excessConfirmed) {
          dispatch(
            openMessageModal({
              buttonText: 'Close',
              modalType: 'WARN',
              title: 'Partial payment completed',
              subTitle:
                'Invoices were paid successfully, but the excess open payment failed. Please check your Transaction History.',
              onClose: async () => {
                if (selectedMainBuyerId) {
                  navigate(`/companies/${selectedMainBuyerId}/payments`);
                  navigate(0);
                  handleClose();
                }
              },
            })
          );
          return;
        }
        // Both legs have landed — retire both keys together.
        clearPayInvoicesKey();
        clearFixedAmountKey();
        showSuccess(
          (paymentResponse.payload as any)?.isReplay === true &&
            (excessResponse.payload as any)?.isReplay === true
        );
      } else if (excessResponse.type === 'payments/payFixedAmount/rejected') {
        dispatch(
          openMessageModal({
            buttonText: 'Close',
            modalType: 'WARN',
            title: 'Partial payment completed',
            subTitle: `Invoices were paid successfully, but the excess open payment failed: ${
              excessResponse.payload as any
            }`,
            onClose: async () => {
              if (selectedMainBuyerId) {
                navigate(`/companies/${selectedMainBuyerId}/payments`);

                handleClose();
              }
            },
          })
        );
      } else {
        dispatch(closeMessageModal());
      }
    } else if (paymentResponse.type === 'payments/pay/rejected') {
      showError(paymentResponse.payload as any);
    } else {
      dispatch(closeMessageModal());
    }
  };

  const handleDeletePaymentMethod = async (paymentMethod: DillPaymentMethod) => {
    const isBankAccount = paymentMethod.type === 'BANK_ACCOUNT';
    const warningMessage = isBankAccount
      ? 'This action cannot be undone. If you delete a bank method and decide to re-add the same one, you will need to wait 48 hours.'
      : 'This action can not be undone.';
    dispatch(
      openMessageModal({
        buttonText: 'Cancel',
        isSecondButton: true,
        secondButtonText: 'Delete',
        modalType: 'WARN',
        title: `Are you sure you want to delete this payment method?`,
        subTitle: warningMessage,
        onSecondButtonClick: async () => {
          await appDelay(10);
          dispatch(
            openMessageModal({
              buttonText: '',
              modalType: 'LOADING',
              title: 'Deleting',
              subTitle: 'Deleting payment method',
            })
          );
          const deletePaymentMethodResults = await dispatch(
            deletePaymentMethodByMainBuyer({
              mainBuyerId: selectedMainBuyerId || '',
              paymentMethodId: paymentMethod.id,
            })
          );
          if (deletePaymentMethodResults.type === 'payments/deletePaymentMethod/rejected') {
            let responseErrorMessage = '';
            if (deletePaymentMethodResults.type === 'payments/deletePaymentMethod/rejected') {
              responseErrorMessage = deletePaymentMethodResults.payload as any;
            }
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'ERROR',
                title: 'Error deleting payment method',
                subTitle: `${responseErrorMessage} Contact (628) 304-0931 for customer support`,
              })
            );
          } else if (deletePaymentMethodResults.type === 'payments/deletePaymentMethod/fulfilled') {
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'SUCCESS',
                title: 'Payment method deleted',
                subTitle: `Payment method deleted successfully`,
                onClose: () => {
                  dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId || '' }));
                },
              })
            );
          } else {
            closeMessageModal();
          }
        },
      })
    );
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        handleClose();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <div className="flex min-h-80 w-[90vw] md:w-5/12 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg flex-col px-3 py-1">
        <AppAddPaymentMethodModal
          userType="buyer"
          open={isAppAddPaymentMethodModalOpen}
          paymentOptionsToShow={
            selectedSupplierForBuyer?.isOptoutCardPayments
              ? 'BANK'
              : selectedSupplier?.cardPaymentProvider === 'FISERV'
              ? 'BANK_AND_FISERV_CARD'
              : 'ALL'
          }
          isDebitCardDisabled={
            selectedSupplierForBuyer?.isDebitCardDisabled ||
            (buyerForMainBuyer &&
              buyerForMainBuyer?.disabledPaymentTypes &&
              buyerForMainBuyer?.disabledPaymentTypes?.includes('DEBIT_CARD'))
              ? true
              : false
          }
          isCreditCardDisabled={
            buyerForMainBuyer &&
            buyerForMainBuyer?.disabledPaymentTypes &&
            buyerForMainBuyer?.disabledPaymentTypes?.includes('CREDIT_CARD')
              ? true
              : false
          }
          isBankDisabled={
            buyerForMainBuyer &&
            buyerForMainBuyer?.disabledPaymentTypes &&
            buyerForMainBuyer?.disabledPaymentTypes?.includes('BANK')
              ? true
              : false
          }
          handleClose={() => {
            dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId || '' }));
            setIsAppAddPaymentMethodModalOpen(false);
          }}
        />
        <PaymentConfirmationModal
          open={isPaymentConfirmationModalOpen}
          handleClose={() => {
            setIsPaymentConfirmationModalOpen(false);
          }}
          paymentDetails={
            selectedPaymentMethod && selectedMainBuyer
              ? {
                  paymentFrom: selectedMainBuyer?.name.toLocaleLowerCase(),
                  paymentMethod: selectedPaymentMethod,
                  paymentTo: (selectedSupplierForBuyer?.name || '')?.toLocaleLowerCase(),
                  totalPayment: (paymentAmount || 0) + fees,
                }
              : undefined
          }
          isApplyToOldest={isApplyToOldest}
          invoiceCount={distributionResult.distributedInvoices.length}
          totalDiscount={distributionResult.totalDiscount}
          invoiceSubtotal={distributionResult.totalInvoiceAmount}
          creditsApplied={distributionResult.totalCreditsUsed}
          excessAmount={distributionResult.excessAmount}
          handlePayNow={() => {
            handlePay();
          }}
        />

        <AppVerifyDwollaMicroTransactionsModal
          selectedPaymentMethod={selectedPaymentMethodToVerifyMicroTransactions}
          open={isAppVerifyDwollaMicroTransactionsModalOpen}
          handleClose={() => {
            dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId || '' }));
            setSelectedPaymentMethodToVerifyMicroTransactions(undefined);
            setIsAppVerifyDwollaMicroTransactionsModalOpen(false);
          }}
        />

        <div className="flex justify-between items-center mb-2 ">
          <div className="flex">
            <h3 className="font-medium text-lg">Pay Fixed Amount</h3>
          </div>
          <AppButton
            type="FLAT"
            buttonStyles={{ padding: '0px', margin: '5px 2px' }}
            icon={<X color={COLORS.GREY_300} size={30} />}
            onClick={handleClose}
          />
        </div>
        <div className="w-full mb-3 px-3">
          <div className="flex flex-col overflow-y-scroll max-h-[80vh] relative">
            <div className="w-full my-2">
              <AppInputField
                prefix="$"
                label="Amount"
                isRequired={true}
                inputType="number"
                errorText={
                  isShowErrors && paymentAmount === 0
                    ? 'Amount has to be greater than 0'
                    : isShowErrors && (paymentAmount === null || paymentAmount === undefined)
                    ? 'Required'
                    : ''
                }
                value={paymentAmount || ''}
                onBlur={(e) => {
                  let val = e.target.value;
                  if (!val) {
                    val = null;
                  } else {
                    val = +Number(Number(val)).toFixed(2);
                  }
                  setPaymentAmount(val);
                }}
                onTextChange={(text) => {
                  setPaymentAmount(text ? Number(text) : null);
                }}
              />
            </div>

            <div className="w-full my-2">
              <div className="flex flex-row items-center justify-between">
                <p className="text-sm font-bold ">Select a Payment Method</p>

                <AppButton
                  text="New Method"
                  type="FLAT"
                  icon={<Plus size={16} color={COLORS.PRIMARY_500} />}
                  onClick={() => {
                    // onChangeModalType('newPaymentMethod');
                    setIsAppAddPaymentMethodModalOpen(true);
                  }}
                />
              </div>
              <div className="flex flex-col grow overflow-y-scroll max-h-64">
                {paymentMethods
                  .filter((pm) => {
                    if (
                      pm.provider === 'DWOLLA' &&
                      buyerForMainBuyer &&
                      buyerForMainBuyer.disabledPaymentTypes &&
                      buyerForMainBuyer.disabledPaymentTypes.includes('BANK')
                    ) {
                      return false;
                    }
                    if (
                      pm.type === 'CREDIT_CARD' &&
                      selectedSupplierForBuyer?.isOptoutCardPayments
                    ) {
                      return false;
                    }
                    if (
                      pm.cardType &&
                      pm.cardType !== 'CREDIT' &&
                      selectedSupplierForBuyer?.isDebitCardDisabled
                    ) {
                      return false;
                    }
                    if (
                      pm.cardType &&
                      pm.cardType === 'CREDIT' &&
                      buyerForMainBuyer &&
                      buyerForMainBuyer.disabledPaymentTypes &&
                      buyerForMainBuyer.disabledPaymentTypes.includes('CREDIT_CARD')
                    ) {
                      return false;
                    }
                    if (
                      pm.cardType &&
                      pm.cardType !== 'CREDIT' &&
                      buyerForMainBuyer &&
                      buyerForMainBuyer.disabledPaymentTypes &&
                      buyerForMainBuyer.disabledPaymentTypes.includes('DEBIT_CARD')
                    ) {
                      return false;
                    }
                    if (pm.provider === 'DWOLLA') {
                      return true;
                    }
                    if (
                      pm.provider === 'STRIPE' &&
                      (selectedSupplierForBuyer?.cardPaymentProvider === 'STRIPE' ||
                        !selectedSupplierForBuyer?.cardPaymentProvider)
                    ) {
                      return true;
                    }
                    if (
                      pm.provider === 'FISERV' &&
                      selectedSupplierForBuyer?.cardPaymentProvider === 'FISERV'
                    ) {
                      return true;
                    }
                    return false;
                  })
                  .map((paymentMethod, i) => {
                    return (
                      <div
                        key={`${paymentMethod.id}_list`}
                        className={'w-full px-1 my-1 relative'}
                        data-tooltip-id={`payment-method-tooltip_${paymentMethod.id}`}>
                        <div className="absolute right-5 top-2">
                          <Trash
                            size={16}
                            className="cursor-pointer"
                            color={COLORS.ERROR_500}
                            onClick={() => {
                              handleDeletePaymentMethod(paymentMethod);
                            }}
                          />
                        </div>
                        <div
                          className={twMerge(
                            'flex flex-row rounded-xl  px-5 py-4 border justify-between items-center cursor-pointer ',
                            paymentMethod.id === selectedPaymentMethod?.id
                              ? 'PRIMARY_500-BORDER'
                              : ' GREY_300-BORDER',
                            paymentMethod.status === 'MICRO_DEPOSITS_SENT' ||
                              paymentMethod.status === 'MICRO_DEPOSITS_VERIFICATION_PENDING' ||
                              paymentMethod.status === 'UNVERIFIED'
                              ? 'bg-gray-100'
                              : 'WHITE-BG'
                          )}
                          onClick={() => {
                            if (!paymentMethod?.status) {
                              setSelectedPaymentMethod(paymentMethod);
                            }
                          }}>
                          <div className="flex flex-row ">
                            <div className="flex mr-2 items-center">
                              <AppRadioButton
                                isChecked={paymentMethod.id === selectedPaymentMethod?.id}
                              />
                              <div className="flex justify-center items-center w-[34px] md:w-[64px] md:h-[40px] GREY_100-BG ml-4">
                                {paymentMethod.type === 'BANK_ACCOUNT' ? (
                                  <Bank
                                    size={20}
                                    style={{ display: 'flex', alignSelf: 'center' }}
                                  />
                                ) : (
                                  <CreditCard
                                    size={30}
                                    style={{ display: 'flex', alignSelf: 'center' }}
                                  />
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col">
                              {paymentMethod.type === 'BANK_ACCOUNT' ? (
                                <div className="text-sm font-bold text-left">Bank Transfer</div>
                              ) : paymentMethod.type === 'CREDIT_CARD' ? (
                                <div className="text-sm font-bold text-left">
                                  {paymentMethod.cardType === 'CREDIT'
                                    ? 'Credit Card'
                                    : 'Debit / Other Card'}
                                </div>
                              ) : (
                                <div className="text-sm font-bold text-left">
                                  Debit / Other Card
                                </div>
                              )}
                              <p className="font-normal text-xs GREY_500-CLR">{`${paymentMethod.paymentMethodName}`}</p>
                              {paymentMethod?.expiry && (
                                <p className="font-normal text-xs GREY_500-CLR">{`Expires ${moment(
                                  paymentMethod.expiry,
                                  paymentMethod.expiry.length === 4 ? 'MMYY' : 'YYYYM'
                                ).format('MM/YY')}`}</p>
                              )}
                              {paymentMethod?.user?.email && (
                                <p className="text-2xs  text-left">{paymentMethod?.user?.email}</p>
                              )}
                            </div>
                          </div>

                          {paymentMethod.type === 'CREDIT_CARD' &&
                          paymentMethod.cardType === 'CREDIT' &&
                          paymentMethod.provider === 'STRIPE' &&
                          creditCardTakeRate &&
                          creditCardTakeRate > 0 ? (
                            <div className="flex-end">
                              <div className="text-xs GREY_500-CLR font-normal ">Fees</div>
                              <div className="text-sm PRIMARY_500-CLR font-semibold ">{`${creditCardTakeRate}%`}</div>
                            </div>
                          ) : paymentMethod.type === 'CREDIT_CARD' &&
                            paymentMethod.cardType === 'CREDIT' &&
                            paymentMethod.provider === 'FISERV' &&
                            selectedSupplierForBuyer &&
                            getBuyerFiservFeeRate({
                              supplier: selectedSupplierForBuyer,
                              buyer: selectedSupplierForBuyer.buyer,
                            }) > 0 ? (
                            <div className="flex-end">
                              <div className="text-xs GREY_500-CLR font-normal ">Fees</div>
                              <div className="text-sm PRIMARY_500-CLR font-semibold ">{`${getBuyerFiservFeeRate(
                                {
                                  supplier: selectedSupplierForBuyer,
                                  buyer: selectedSupplierForBuyer.buyer,
                                }
                              )}%`}</div>
                            </div>
                          ) : (
                            <></>
                          )}
                          {(paymentMethod.status === 'MICRO_DEPOSITS_SENT' ||
                            paymentMethod.status === 'MICRO_DEPOSITS_VERIFICATION_PENDING' ||
                            paymentMethod.status === 'UNVERIFIED') && (
                            <div className="flex-end">
                              <div className="text-xs flex items-center mb-[2px]">
                                <Warning size={18} color={COLORS.ERROR_500} className="mr-1" />
                                Unverified
                              </div>

                              <div className="">
                                <AppButton
                                  text="Verify Deposits"
                                  isDisabled={
                                    paymentMethod.status !== 'MICRO_DEPOSITS_VERIFICATION_PENDING'
                                  }
                                  onClick={() => {
                                    setIsAppVerifyDwollaMicroTransactionsModalOpen(true);
                                    setSelectedPaymentMethodToVerifyMicroTransactions(
                                      paymentMethod
                                    );
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
              {isShowErrors && !selectedPaymentMethod && (
                <div className="text-red-500 text-xs my-1">Please select a payment method</div>
              )}
            </div>

            <div className="w-full my-2">
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center gap-2">
                  <AppSwitch
                    isChecked={isApplyToOldest}
                    onSwitchChange={(value: boolean) => setIsApplyToOldest(value)}
                  />
                  <span className="text-sm font-medium TEXT_PRIMARY-CLR">
                    Apply to oldest invoices
                  </span>
                </div>
              </div>
              {isApplyToOldest && openInvoices.length === 0 && (
                <div className="flex items-center gap-1 mt-2 text-sm text-gray-500">
                  <Info size={16} />
                  <span>No open invoices available for this supplier</span>
                </div>
              )}

              {isShowErrors && isApplyToOldest && openInvoices.length === 0 && (
                <div className="text-red-500 text-xs mt-1">
                  No open invoices available to apply payment
                </div>
              )}
            </div>

            {!isApplyToOldest && (
              <div className=" w-full my-2">
                <AppInputField
                  label="Remittance Notes"
                  placeholder=""
                  value={remittanceNotes}
                  errorText={isShowErrors && !remittanceNotes ? 'Required' : ''}
                  inputType="textarea"
                  isRequired={true}
                  onTextChange={(text) => {
                    setRemittanceNotes(text);
                  }}
                />
              </div>
            )}

            <div className="flex flex-col justify-between  mt-4 rounded-xl PRIMARY_50-BG px-5 py-4  bottom-0 sticky">
              <div>
                <div className="flex flex-row justify-between  mb-2 ">
                  <div className="GREY_500-CLR font-semibold">Amount</div>
                  <div className="font-semibold TEXT_PRIMARY-CLR">{`${formatMoney(
                    paymentAmount || 0,
                    2
                  )}`}</div>
                </div>
              </div>
              {fees !== 0 && (
                <div className="flex flex-row justify-between w-full pb-2">
                  <div className="GREY_500-CLR font-medium w-1/5">Fees</div>
                  <div className="font-semibold TEXT_PRIMARY-CLR">{`+${formatMoney(fees, 2)}`}</div>
                </div>
              )}
              <div className="flex flex-row justify-between w-full pt-2  border-t">
                <div className="font-semibold TEXT_PRIMARY-CLR">Total Payment</div>
                <div className="PRIMARY_500-CLR font-semibold">{`${formatMoney(
                  (paymentAmount || 0) + fees,
                  2
                )}`}</div>
              </div>
            </div>
          </div>

          <div className="mt-3 w-full flex justify-end ">
            <div className=" flex">
              <div className="mr-2">
                <AppButton text="Close" type="TERTIARY" onClick={handleClose} />
              </div>

              <AppButton
                text="Pay"
                onClick={() => {
                  if (checkErrors()) {
                    return;
                  }
                  setIsPaymentConfirmationModalOpen(true);
                }}
              />
            </div>
          </div>

          {paymentMethods
            .filter(
              (pymt) =>
                pymt.status === 'MICRO_DEPOSITS_SENT' ||
                pymt.status === 'MICRO_DEPOSITS_VERIFICATION_PENDING' ||
                pymt.status === 'UNVERIFIED'
            )
            .map((paymentMethod) => {
              return (
                <ReactTooltip
                  id={`payment-method-tooltip_${paymentMethod.id}`}
                  place="top"
                  variant="dark"
                  key={`${paymentMethod.id}ii`}
                  opacity={'100%'}
                  style={{
                    display: 'flex',
                    background: '#334155',
                    width: '300px',
                    zIndex: '70',
                    borderRadius: '8px',
                    alignItems: 'center',
                    justifyItems: 'center',
                    fontWeight: '600',
                  }}>
                  {paymentMethod.status === 'MICRO_DEPOSITS_VERIFICATION_PENDING' ? (
                    <span className="text-xs">
                      Two micro-deposits have arrived in your account. Please check for these
                      deposits in your account and verify them in order to use this bank method.
                      This is a one-time setup for this bank account.
                    </span>
                  ) : (
                    <span className="text-xs">
                      Two micro-deposits were sent to this bank account. They should arrive 1-2
                      business days from the day it was added. Once you see these deposits in your
                      account, please verify them in order to use this bank method. This is a
                      one-time setup for this bank account.
                    </span>
                  )}
                </ReactTooltip>
              );
            })}
        </div>
      </div>
    </Modal>
  );
};
